import { readShownRewardPoints } from "./aiAgentStateReader";

const PROTOCOL_VERSION = "gf-agent-ws/v1";

function bombShown() {
  return document.getElementById("bomb image") !== null; // game.js's id for the revealed bomb
}

function crashShown(game) {
  return game?.driverImage?.src?.includes("collisionDriver") === true;
}

// performance.now() -> Unix epoch ms, so the timestamps join with the agent server's logs.
function toEpochMs(performanceMs) {
  return Math.round(performance.timeOrigin + performanceMs);
}

/**
 * GF04's side of the latency: from the first frame the choice was open to the moment its key was
 * dispatched. The agent measures its own decision time; this covers the whole round trip.
 */
function buildTiming(choiceOnsetMs, appliedMs) {
  if (choiceOnsetMs == null || appliedMs == null) return null;
  return {
    choiceOnsetAt: toEpochMs(choiceOnsetMs),
    actionAppliedAt: toEpochMs(appliedMs),
    choiceOnsetToActionMs: Math.round(appliedMs - choiceOnsetMs),
  };
}

/**
 * Turns applied agent commands into `trial.result` messages, sent once the public outcome is on
 * screen - never earlier, and never with information the player didn't get. Only commands for a
 * research choice (a snapshot with `agentObservation`) are tracked; the fixed-action simulator
 * doesn't use results.
 *
 * - `attend` (Space on the approach) resolves right away as `locked_on` or `no_lock_on`. A reply
 *   that arrives too late is dropped as stale (aiAgentSocket, not this file) unless the driver
 *   crashed while it was in flight, in which case resolveStaleAttend below still reports it.
 * - `continue` on an HQ message resolves right away as `continued`; it is never scored.
 * - A lane choice resolves when the "+N"/"-N" shows over the driver car, with that number as the
 *   reward. If the trial ends first (crash, timeout) the reward is null - no points were shown,
 *   which is not the same as 0.
 */
export class AiAgentResultTracker {
  constructor({ send, logMessages = true }) {
    this.send = send;
    this.logMessages = logMessages;
    this.pending = null;
  }

  /**
   * Applies a command through `press` and starts tracking its result.
   * @param {{decisionId: string, action: object, kind: "attend" | "hq_message" | "choice" | null, choiceOnsetMs: number | null, game: object, press: () => number}} command
   */
  apply({ decisionId, action, kind, choiceOnsetMs = null, game, press }) {
    if (!kind) {
      press();
      return;
    }
    // One choice is open at a time, so a new command means the previous one is over.
    if (this.pending) this.resolve(null, crashShown(this.pending.game) ? "crash" : "no_points_shown");

    if (kind === "attend") {
      // driveUpKeypress acts on Space synchronously, and only inside the proximity zone.
      const lockedOn = game?.checkProximityZone?.() === true;
      const timing = buildTiming(choiceOnsetMs, press());
      this.emit({ decisionId, action, timing }, null, lockedOn ? "locked_on" : "no_lock_on");
      return;
    }
    if (kind === "hq_message") {
      const timing = buildTiming(choiceOnsetMs, press());
      this.emit({ decisionId, action, timing }, null, "continued");
      return;
    }
    const timing = buildTiming(choiceOnsetMs, press());
    this.pending = { decisionId, action, timing, game };
  }

  /**
   * A stale attend command still deserves feedback when the reason it went stale is a crash - the
   * agent was asked to attend, its reply came back too late, and the driver hit the target before
   * the command arrived. Any other stale reason (the game simply moved to the next choice) stays a
   * silent drop, same as a keypress nobody is listening for anymore.
   * @returns {boolean} true if this resolved the command (caller should skip its own stale-drop log)
   */
  resolveStaleAttend({ decisionId, action, kind, game }) {
    if (kind !== "attend" || !crashShown(game)) return false;
    this.emit({ decisionId, action }, null, "crash");
    return true;
  }

  /** Call on every state change, with the Game currently on screen. */
  observe(currentGame) {
    if (!this.pending) return;
    if (currentGame !== this.pending.game) {
      this.resolve(null, crashShown(this.pending.game) ? "crash" : "no_points_shown");
      return;
    }
    const points = readShownRewardPoints();
    if (points === null) return;
    this.resolve(points, bombShown() ? "bomb" : this.pending.game.emotion ?? null);
  }

  resolve(reward, outcome) {
    const { decisionId, action, timing } = this.pending;
    this.pending = null;
    this.emit({ decisionId, action, timing }, reward, outcome);
  }

  emit({ decisionId, action, timing = null }, reward, outcome) {
    const message = {
      type: "trial.result",
      protocolVersion: PROTOCOL_VERSION,
      decisionId,
      status: "applied",
      action,
      feedback: { outcome },
      // GF04 always moves on to another choice until the mission ends.
      nextDecisionPending: true,
    };
    if (reward !== null) message.reward = reward;
    // Left out when the command was never applied (a stale attend resolved as a crash).
    if (timing) message.timing = timing;
    if (this.logMessages) console.log("[aiAgentSocket] trial.result", message);
    this.send(message);
  }

  reset() {
    this.pending = null;
  }
}
